import { useState, useContext, useEffect } from "react";
import AuthContext from "../../lib/auth-context";
import moment from "moment";
import ThemeContext from "../../lib/theme-context";
import ResultsTrackerLogic from "../results-tracker/logic";
import { getJSON } from "../../utils/safeLocalStorage";

const Logic = () => {
  const [trades, setTrades] = useState([]);
  const auth = useContext(AuthContext);
  const mode = localStorage.getItem("equity-mode");
  const year = localStorage.getItem("year");
  const theme = useContext(ThemeContext);
  const { calculateFields } = ResultsTrackerLogic();

  // Force re-render when account changes by using a counter
  const [accountChangeCounter, setAccountChangeCounter] = useState(0);
  const [backtestChangeCounter, setBacktestChangeCounter] = useState(0);
  
  useEffect(() => {
    const handleAccountChange = () => {
      setAccountChangeCounter(prev => prev + 1);
    };
    
    const handleBacktestChange = () => {
      setBacktestChangeCounter(prev => prev + 1);
    };
    
    const handleSettingsChange = () => {
      const skipPreviousYears = localStorage.getItem("skipPreviousYears") === "true";
      const currentYear = String(new Date().getFullYear());
      const currentYearSelection = localStorage.getItem("year");
      
      // Reset year selection if it's no longer valid
      if (skipPreviousYears && currentYearSelection !== "0" && currentYearSelection !== currentYear) {
        localStorage.setItem("year", "0");
        setAccountChangeCounter(prev => prev + 1); // Trigger re-render
      }
    };
    
    // Listen for custom events
    window.addEventListener('account-changed', handleAccountChange);
    window.addEventListener('backtest-changed', handleBacktestChange);
    window.addEventListener('settings-changed', handleSettingsChange);
    
    return () => {
      window.removeEventListener('account-changed', handleAccountChange);
      window.removeEventListener('backtest-changed', handleBacktestChange);
      window.removeEventListener('settings-changed', handleSettingsChange);
    };
  }, []);

  useEffect(() => {
    let all_trades;

    if (auth.backtestingMode) {
      // Further filter by selected backtest first
      const selectedBacktest = localStorage.getItem("backtest-active");
      
      // Use auth.trades for backtest mode (auth.allTrades only has live trades)
      const tradesSource = auth.trades || [];
      
      // If no backtest is selected, return empty array
      if (!selectedBacktest || selectedBacktest === "null" || selectedBacktest === "") {
        all_trades = [];
      } else if (selectedBacktest === "Missed Trades") {
        // Handle missed trades case
        all_trades = tradesSource.filter((trades) =>
          trades?.id?.includes("backtest-") && trades.missedTrade
        );
      } else {
        // Handle specific backtest selection
        all_trades = tradesSource.filter((trades) =>
          trades?.id?.includes("backtest-") && trades.testId === selectedBacktest
        );
      }
    } else {
      all_trades = auth.allTrades.filter((trades) =>
        trades?.id?.includes("live-")
      );
      
      // Filter by selected account
      const selectedAccountId = localStorage.getItem("selected-account-id") || "All";
      
      if (selectedAccountId !== "All") {
        // Get user data to find account info
        const user = getJSON("user", {});
        const capitalAccounts = user.capitalAccounts || [];
        
        if (selectedAccountId === "All Personal") {
          // Filter for personal accounts only
          const personalAccountIds = capitalAccounts
            .filter(acc => acc.type === "Personal" && acc.isActive)
            .map(acc => acc.id);
          all_trades = all_trades.filter(trade => 
            trade.accountId && personalAccountIds.includes(trade.accountId)
          );
        } else if (selectedAccountId === "All Investor") {
          // Filter for investor accounts only
          const investorAccountIds = capitalAccounts
            .filter(acc => acc.type === "Investor" && acc.isActive)
            .map(acc => acc.id);
          all_trades = all_trades.filter(trade => 
            trade.accountId && investorAccountIds.includes(trade.accountId)
          );
        } else {
          // Filter for specific account
          all_trades = all_trades.filter(trade => 
            trade.accountId === selectedAccountId
          );
        }
      }
    }

    // Use pre-calculated trades from auth.allTrades (no need to recalculate)
    setTrades(all_trades);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, year, accountChangeCounter, backtestChangeCounter, auth.allTrades, auth.backtestingMode, auth.trades]);

  // NOTE: Calculation logic has been moved to server-side centralized service
  // All calculations now happen in /server/lib/tradeCalculations.js
  // Trades use pre-calculated DB fields for performance
  const runCalculations = async (_trades) => {
    let trades = _trades;

    // Only filter by account when not in backtesting mode
    if (!auth.backtestingMode) {
      const selectedAccountId = localStorage.getItem("selected-account-id") || "All";
      if (selectedAccountId === "All Personal") {
        trades = trades.filter((trade) => trade.equityType === "Personal Capital");
      } else if (selectedAccountId === "All Investor") {
        trades = trades.filter((trade) => trade.equityType === "Investor Capital");
      } else if (selectedAccountId !== "All") {
        trades = trades.filter((trade) => trade.accountId === selectedAccountId);
      }
    }

    const selectedBacktest = localStorage.getItem("backtest-active");
    if (localStorage.getItem("year") !== "0" && (!auth.backtestingMode || selectedBacktest === "Missed Trades")) {
      const currentYearSet = moment(localStorage.getItem("year")).format("YY");

      trades = trades.filter((t, index) => {
        let dateInput = t.entryDate; // DD/MM/YY format

        // Skip year filtering for trades without entryDate or with invalid date format
        if (!dateInput || dateInput === "") {
          return t;
        }

        let dateArray = dateInput.split("/");

        // Handle different date formats
        if (dateArray.length < 3) {
          return t;
        }

        let newDate = dateArray[2];

        if (Number(newDate) === Number(currentYearSet)) {
          return t;
        } else {
          return false;
        }
      });
    }

    let virtualArray = [];

    let totalOutcomesPC = [];
    let totalOutcomesIC = [];

    let returnDollarTotals_Investor_summed;
    let returnDollarTotals_Personal_summed;

    let returnPercentTotalsIC = [];
    let returnDollarTotalsInvestor = [];

    let returnPercentTotalsPC = [];
    let returnDollarTotalsPersonal = [];

    let profitLossDollarInvestor;
    let profitLossPercentageIC;
    let profitLossDollarPersonal;
    let profitLossPercentagePC;
    let allFeesIC = [];
    let allFeesPC = [];

        
    // For backtest mode, include all trades regardless of equityType
    if (!auth.backtestingMode) {
      if (mode === "Personal Capital") {
        trades = trades.filter(
          (trades) => trades?.equityType === "Personal Capital"
        );
              }
      if (mode === "Investor Capital") {
        trades = trades.filter(
          (trades) => trades?.equityType === "Investor Capital"
        );
              }
    } else {
          }
   
    

    for (let index = 0; index < trades.length; index++) {
      const trade = trades[index];
      const capital = trade.equityType;

      if (capital === "Investor Capital") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippageIC = trade.outcomeIC;
        profitLossPercentageIC = parseFloat(trade.profitLossPercentageIC || 0);

        profitLossDollarInvestor = parseFloat(trade.profitLossDollarInvestor || 0);
        allFeesIC.push((trade.netDollarIC || 0) - profitLossDollarInvestor);

        returnPercentTotalsIC.push(profitLossPercentageIC);
        returnDollarTotalsInvestor.push(profitLossDollarInvestor);
        totalOutcomesIC.push(outcomeOfSlippageIC);

        const schema = {
          timezone: trade.timezone,
          id: trade.id,
          _id: trade._id,
          profitLossDollarInvestor: trade.profitLossDollarInvestor,
          profitLossDollarPersonal: 0,
          profitLossPercentagePC: 0,
          profitLossPercentageIC: parseFloat(profitLossPercentageIC).toFixed(2),
          outcomePC: "",
          outcomeIC: outcomeOfSlippageIC,
          openPricePC: 0,
          closePricePC: 0,
          openPriceIC: trade.openPriceIC,
          closePriceIC: trade.closePriceIC,
          pair: trade.pair,
          risk: trade.risk,
          direction: trade.direction,
          stopLossPC: 0,
          stopLossIC: trade.stopLossIC,
          entryDate: trade.entryDate,
          entryTime: trade.entryTime,
          exitDate: trade.exitDate,
          exitTime: trade.exitTime,
          feePC: trade.feePC,
          feeIC: trade.feeIC,
          swapPC: trade.swapPC,
          swapIC: trade.swapIC,
          betaPattern: trade.betaPattern,
          alphaPattern: trade.alphaPattern,
          
          foreCastId: trade.foreCastId,
          min5_before: trade.min5_before,
          min15_before: trade.min15_before,
          hour1_before: trade.hour1_before,
          hour1_after: trade.hour1_after,
          hour4_before: trade.hour4_before,
          hour4_after: trade.hour4_after,
          daily_before: trade.daily_before,
          daily_after: trade.daily_after,
          weekly_before: trade.weekly_before,
          weekly_after: trade.weekly_after,
          journalComplete: trade.journalComplete,
          journalID: trade.journalID,
          entryType: trade.entryType,
          grade: trade.grade,
          highlightedPair: trade.highlightedPair,
          firstEntryOrScaleIn: trade.firstEntryOrScaleIn,
          testId:
            trade.missedTrade ? "All Missed Trades" : trade.testId,
          missedTrade: trade.missedTrade,
          returnTotalsPersonal: 0,
          returnTotalsInvestor: returnDollarTotals_Investor_summed?.toFixed(2),
          created: trade.created,
          equityType: trade.equityType,
          netDollarPC: 0,
          netDollarIC: trade.netDollarIC || 0,
        };
        virtualArray.push(schema);
      } else if (capital === "Personal Capital" || capital === "Backtest") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippagePC = trade.outcomePC;
        profitLossPercentagePC = parseFloat(trade.profitLossPercentagePC || 0);

        profitLossDollarPersonal = parseFloat(trade.profitLossDollarPersonal || 0);
        allFeesPC.push((trade.netDollarPC || 0) - profitLossDollarPersonal);

        returnPercentTotalsPC.push(profitLossPercentagePC);
        returnDollarTotalsPersonal.push(profitLossDollarPersonal);

        totalOutcomesPC.push(outcomeOfSlippagePC);

        const schema = {
          timezone: trade.timezone,
          id: trade.id,
          _id: trade._id,
          profitLossDollarInvestor: trade.profitLossDollarInvestor,
          profitLossDollarPersonal: trade.profitLossDollarPersonal,
          profitLossPercentagePC: parseFloat(profitLossPercentagePC).toFixed(2),
          profitLossPercentageIC: 0,
          outcomePC: outcomeOfSlippagePC,
          outcomeIC: "",
          openPricePC: trade.openPricePC,
          closePricePC: trade.closePricePC,
          openPriceIC: trade.openPriceIC,
          closePriceIC: trade.closePriceIC,
          pair: trade.pair,
          risk: trade.risk,
          direction: trade.direction,
          stopLossPC: trade.stopLossPC,
          stopLossIC: trade.stopLossIC,
          entryDate: trade.entryDate,
          entryTime: trade.entryTime,
          exitDate: trade.exitDate,
          exitTime: trade.exitTime,
          feePC: trade.feePC,
          feeIC: trade.feeIC,
          swapPC: trade.swapPC,
          swapIC: trade.swapIC,
          betaPattern: trade.betaPattern,
          alphaPattern: trade.alphaPattern,
          
          foreCastId: trade.foreCastId,
          min5_before: trade.min5_before,
          min15_before: trade.min15_before,
          hour1_before: trade.hour1_before,
          hour1_after: trade.hour1_after,
          hour4_before: trade.hour4_before,
          hour4_after: trade.hour4_after,
          daily_before: trade.daily_before,
          daily_after: trade.daily_after,
          weekly_before: trade.weekly_before,
          weekly_after: trade.weekly_after,
          journalComplete: trade.journalComplete,
          journalID: trade.journalID,
          entryType: trade.entryType,
          grade: trade.grade,
          highlightedPair: trade.highlightedPair,
          firstEntryOrScaleIn: trade.firstEntryOrScaleIn,
          testId:
            trade.missedTrade ? "All Missed Trades" : trade.testId,
          missedTrade: trade.missedTrade,
          returnTotalsPersonal: returnDollarTotals_Personal_summed?.toFixed(2),
          returnTotalsInvestor: 0,
          created: trade.created,
          equityType: trade.equityType,
          netDollarPC: trade.netDollarPC || 0,
          netDollarIC: 0,
          // newTransaction: newTransaction,
          // transanctionNotes: note,
          // transanctionReason: reason,
        };
        virtualArray.push(schema);
      } 
    } // end of for loop

    // --------------------------------- ///

    setTrades(virtualArray);
  };
  const runSpeficCal = (_trade, topBorder, showGross = false) => {
    let profitLossPercentageIC;
    let profitLossPercentagePC;
    let totalIC = [];
    let totalPC = [];

    // Get showGross from localStorage if not provided
    if (showGross === undefined || showGross === null) {
      showGross = localStorage.getItem("showGross") === "true";
    }


    for (let index = 0; index < _trade.length; index++) {
      const trade = _trade[index];
      const capital = trade.equityType;

      if (capital === "Investor Capital") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippageIC = trade.outcomeIC;
        const grossPercentIC = parseFloat(trade.profitLossPercentageIC || 0);
        const netPercentIC = parseFloat(trade.percentWithFeesIC || 0);

        if (outcomeOfSlippageIC === "Win") {
          profitLossPercentageIC = showGross ? grossPercentIC : netPercentIC;
        } else if (outcomeOfSlippageIC === "Loss") {
          profitLossPercentageIC = showGross ? grossPercentIC : netPercentIC;
          //convert to negative number
          profitLossPercentageIC = -Math.abs(profitLossPercentageIC);
        } else if (outcomeOfSlippageIC === "Breakeven" || outcomeOfSlippageIC === "In Progress") {
          profitLossPercentageIC = showGross ? 0 : netPercentIC;
        }

        totalIC.push(profitLossPercentageIC);
      } else if (capital === "Personal Capital" || capital === "Backtest") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippagePC = trade.outcomePC;
        const grossPercentPC = parseFloat(trade.profitLossPercentagePC || 0);
        const netPercentPC = parseFloat(trade.percentWithFeesPC || 0);

        if (outcomeOfSlippagePC === "Win") {
          profitLossPercentagePC = showGross ? grossPercentPC : netPercentPC;
        } else if (outcomeOfSlippagePC === "Loss") {
          profitLossPercentagePC = showGross ? grossPercentPC : netPercentPC;
          //convert to negative number
          profitLossPercentagePC = -Math.abs(profitLossPercentagePC);
        } else if (outcomeOfSlippagePC === "Breakeven" || outcomeOfSlippagePC === "In Progress") {
          profitLossPercentagePC = showGross ? 0 : netPercentPC;
        }

        totalPC.push(profitLossPercentagePC);
      } 
    }

    const sumedTotalIC =
      totalIC.length > 0
        ? totalIC.reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0
          )
        : 0;

    const sumedTotalPC =
      totalPC.length > 0
        ? totalPC.reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0
          )
        : 0;

    const both = sumedTotalIC + sumedTotalPC;

    const mode = localStorage.getItem("equity-mode");
    const hasBacktest = _trade.some((t) => t.equityType === "Backtest");

    let percentValueToUse;
    if (mode === "Personal Capital" || hasBacktest) {
      percentValueToUse = sumedTotalPC !== 0 ? sumedTotalPC : sumedTotalIC;
    } else if (mode === "Investor Capital") {
      percentValueToUse = sumedTotalIC !== 0 ? sumedTotalIC : sumedTotalPC;
    } else {
      percentValueToUse = both;
    }

    return (
      <td
        style={{
          color:
            percentValueToUse < 0
              ? "#f23645"
              : percentValueToUse > 0
              ? "#26a65d"
              : "#ED9E0E",
          borderTop: topBorder
            ? `1px solid ${theme[0] === "dark" ? "#FCFCFF" : "black"}`
            : "none",
        }}
      >
        {Math.abs(percentValueToUse).toFixed(2)}%
      </td>
    );
  };
  const changeYear = () => {
    const selectedBacktest = localStorage.getItem("backtest-active");
    const currentYear = String(new Date().getFullYear());
    const skipPreviousYears = localStorage.getItem("skipPreviousYears") === "true";
    let yearsToUse;
    
    // If Missed Trades is selected, get years only from missed trades
    if (selectedBacktest === "Missed Trades") {
      const missedTrades = auth.trades.filter((trade) => 
        trade?.id?.includes("backtest-") && trade.missedTrade
      );
      
      // If no missed trades exist, just set year to 0 and return
      if (missedTrades.length === 0) {
        localStorage.setItem("year", "0");
        runCalculations([]); // Pass empty array for no missed trades
        calculateFields([], auth.backtestingMode);
        return;
      }
      
      // Extract unique years from missed trades
      const missedTradeYears = [...new Set(
        missedTrades
          .filter(trade => trade.entryDate)
          .map(trade => {
            const dateArray = trade.entryDate.split("/");
            return dateArray.length >= 3 ? parseInt(dateArray[2]) : null;
          })
          .filter(year => year !== null && !isNaN(year))
      )].sort((a, b) => a - b);
      
      yearsToUse = missedTradeYears.length > 0 ? missedTradeYears : ["0"];
    } else {
      // Use all years for other cases
      yearsToUse = auth.allYearsOfTrades;
    }
    
    // Filter available years based on skipPreviousYears setting
    if (skipPreviousYears) {
      yearsToUse = yearsToUse.filter(y => String(y) === currentYear);
    }
    
    // If no available years (e.g., all years are filtered out), just cycle between current year and All
    if (yearsToUse.length === 0) {
      yearsToUse = [currentYear];
    }
    
    const firstOfYears = yearsToUse[0];
    const lastOfYears = yearsToUse[yearsToUse.length - 1];

    if (year === String(firstOfYears)) {
      if (yearsToUse.length === 1 && String(firstOfYears) === currentYear) {
        // Only current year available, cycle to All
        localStorage.setItem("year", "0");
      } else {
        localStorage.setItem("year", JSON.stringify(Number(firstOfYears) + 1));
      }
    } else if (year === String(lastOfYears)) {
      localStorage.setItem("year", "0");
    } else if (year === "0") {
      localStorage.setItem("year", String(firstOfYears));
    } else {
      localStorage.setItem("year", JSON.stringify(Number(year) + 1));
    }
    
    // Use filtered trades based on current selection
    let filteredTrades;
    if (selectedBacktest === "Missed Trades") {
      filteredTrades = auth.trades.filter((trade) => 
        trade?.id?.includes("backtest-") && trade.missedTrade
      );
    } else if (selectedBacktest && selectedBacktest !== "null" && selectedBacktest !== "") {
      filteredTrades = auth.trades.filter((trade) => 
        trade?.id?.includes("backtest-") && trade.testId === selectedBacktest
      );
    } else if (auth.backtestingMode) {
      filteredTrades = []; // No selection in backtest mode
    } else {
      filteredTrades = auth.trades.filter((trades) =>
        trades?.id?.includes("live-")
      );
    }
    
    const selectedAccountId = localStorage.getItem("selected-account-id") || "All";

    runCalculations(filteredTrades);

    if (selectedBacktest === "Missed Trades") {
      // Missed trades can't be grouped by calculateFields' testId filter,
      // so leave auth.allTrades untouched; runCalculations already set the
      // display trades above.
    } else if (auth.backtestingMode) {
      calculateFields(
        auth.trades,
        true,
        selectedBacktest,
        undefined,
        undefined,
        selectedAccountId
      );
    } else {
      calculateFields(
        auth.trades,
        false,
        undefined,
        undefined,
        undefined,
        selectedAccountId
      );
    }
  };
  const handleMode = () => {
    if (mode === "Personal Capital") {
      localStorage.setItem("equity-mode", "Investor Capital");
      auth.setEquityTypeMode("Investor Capital");
    } else if (mode === "Investor Capital") {
      localStorage.setItem("equity-mode", "Personal Capital");
      auth.setEquityTypeMode("Personal Capital");
    }
    runCalculations(auth.trades);
    calculateFields(
      auth.trades,
      auth.backtestingMode,
      auth.backtestingMode ? localStorage.getItem("backtest-active") : ""
    );
  };
  const runSpeficCalStats = (_trade, type, showGross = false) => {
    let profitLossPercentageIC;
    let profitLossPercentagePC;
    let totalIC = [];
    let totalPC = [];

    // Get showGross from localStorage if not provided
    if (showGross === undefined || showGross === null) {
      showGross = localStorage.getItem("showGross") === "true";
    }

    for (let index = 0; index < _trade.length; index++) {
      const trade = _trade[index];
      const capital = trade.equityType;

      if (capital === "Investor Capital") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippageIC = trade.outcomeIC;
        const grossPercentIC = parseFloat(trade.profitLossPercentageIC || 0);
        const netPercentIC = parseFloat(trade.percentWithFeesIC || 0);

        if (outcomeOfSlippageIC === "Win") {
          profitLossPercentageIC = showGross ? grossPercentIC : netPercentIC;
          profitLossPercentageIC = parseFloat(profitLossPercentageIC);
        } else if (outcomeOfSlippageIC === "Loss") {
          profitLossPercentageIC = showGross ? grossPercentIC : netPercentIC;
          //convert to negative number
          profitLossPercentageIC = -Math.abs(
            parseFloat(profitLossPercentageIC)
          );
        } else if (outcomeOfSlippageIC === "Breakeven" || outcomeOfSlippageIC === "In Progress") {
          if (showGross) {
            profitLossPercentageIC = 0;
          } else {
            profitLossPercentageIC = parseFloat(trade.percentWithFeesIC || 0);
          }
        }

        totalIC.push(profitLossPercentageIC);
      } else if (capital === "Personal Capital" || capital === "Backtest") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippagePC = trade.outcomePC;
        const grossPercentPC = parseFloat(trade.profitLossPercentagePC || 0);
        const netPercentPC = parseFloat(trade.percentWithFeesPC || 0);

        if (outcomeOfSlippagePC === "Win") {
          profitLossPercentagePC = showGross ? grossPercentPC : netPercentPC;
        } else if (outcomeOfSlippagePC === "Loss") {
          profitLossPercentagePC = showGross ? grossPercentPC : netPercentPC;
          //convert to negative number
          profitLossPercentagePC = -Math.abs(profitLossPercentagePC);
        } else if (outcomeOfSlippagePC === "Breakeven" || outcomeOfSlippagePC === "In Progress") {
          profitLossPercentagePC = showGross ? 0 : netPercentPC;
        }

        totalPC.push(profitLossPercentagePC);
      } 
    }

    const both = totalIC.concat(totalPC);

    const mode = localStorage.getItem("equity-mode");
    const hasBacktest = _trade.some((t) => t.equityType === "Backtest");

    let percentValueToUse;
    if (mode === "Personal Capital" || hasBacktest) {
      percentValueToUse = totalPC.length > 0 ? totalPC : totalIC;
    } else if (mode === "Investor Capital") {
      percentValueToUse = totalIC.length > 0 ? totalIC : totalPC;
    } else {
      percentValueToUse = both;
    }

    return percentValueToUse;
  };

  return {
    trades,
    runSpeficCal,
    theme,
    changeYear,
    handleMode,
    auth,
    runSpeficCalStats,
    calculateFields,
  };
};

export default Logic;
